/* eslint-disable max-params */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Store } from '@ngrx/store';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { ButtonDirective, WcgButtonComponent } from '@wcg/frontend-framework';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  EditGridView,
  EditGridViewForm,
  GridType,
  GridView,
  GridViewDialogData,
  GridViewForm,
} from '../../../types';

import { GRID_VIEW_ACTIONS } from '../../../actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { GridViewFacadeService } from '@data-access/grid-views';
import { minTrimmedLengthValidator } from '@data-access/shared';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    ButtonDirective,
    WcgButtonComponent,
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatInputModule,
    MatDividerModule,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
  ],
  selector: 'wcg-grid-view-dialog',
  styleUrls: ['./grid-view.dialog.scss'],
  templateUrl: './grid-view.dialog.html',
})
export class GridViewDialogComponent {
  public readonly dialogProps =
    inject<Omit<GridViewDialogData<GridView[]>, 'config'>>(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(MatDialogRef<GridViewDialogComponent>);
  private readonly _fb = inject(FormBuilder);
  private readonly _store = inject(Store);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _gridViewFacade = inject(GridViewFacadeService);

  viewDialogForm: FormGroup<GridViewForm> = this._createForm();

  gridNames = signal<FormArray<EditGridViewForm>>(
    this.viewDialogForm.get('gridNames') as FormArray<EditGridViewForm>,
  );

  constructor() {
    if (this.dialogProps.viewAction === 'Edit') {
      this._updateViewDialogForm(
        this.dialogProps.data
          ?.slice(0, 5) //This will be removed once the restriction applies
          .map((g) => ({ name: g.name, id: g.id })) as EditGridView[],
      );
    }
    this._customListeners();
  }

  saveEditGridView(): void {
    switch (this.dialogProps.viewAction) {
      case 'Save':
      case 'Create':
        this._store.dispatch(
          GRID_VIEW_ACTIONS.onSaveGridView({
            gridView: this._constructData(
              this.viewDialogForm.get('gridNames')?.value as string,
            ),
            grid_type: this.dialogProps.gridProps?.grid_type as GridType,
          }),
        );
        break;
      case 'Update':
        this._store.dispatch(
          GRID_VIEW_ACTIONS.onUpdateGridView({
            gridView: this._constructData(
              this.dialogProps.gridProps?.name as string,
            ),
            grid_type: this.dialogProps.gridProps?.grid_type as GridType,
          }),
        );
        break;
      case 'Edit':
        this._constructEdit();
        break;
      default:
        console.error('nothing happened');
        this._dialogRef.close();
        break;
    }
  }

  close(): void {
    this._dialogRef.close();
  }

  deleteGridView(index: number): void {
    this.gridNames.update((arr) => {
      arr.removeAt(index);
      return arr;
    });
  }
  // @TODO THE FOLLOWING CODE WILL BE DEPRECATED ONCE THE NEW GRID APPROACH IS IMPLEMENTED
  private _createForm(): FormGroup {
    const gridNames = () => {
      return this.dialogProps.viewAction === 'Save' ||
        this.dialogProps.viewAction === 'Create'
        ? (new FormControl('', [
            Validators.required,
            minTrimmedLengthValidator(3),
          ]) as FormControl<string>)
        : (this._fb.array<EditGridViewForm>([]) as FormArray<EditGridViewForm>);
    };

    return this._fb.group<GridViewForm>({
      gridNames: gridNames(),
    });
  }

  private _constructData(gridName: string): GridView {
    return {
      id: this.dialogProps.gridProps?.id,
      data: this.dialogProps.data as GridView[],
      description: this.dialogProps.gridProps?.description,
      grid_type: this.dialogProps.gridProps?.grid_type as GridType,
      name: gridName.trim(),
    };
  }

  private _customListeners(): void {
    this._gridViewFacade.gridViewSaveFailure
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (error) => (
          this.viewDialogForm.get('gridNames')?.setErrors({
            serverError: error,
          }),
          this._cdRef.markForCheck()
        ),
      });
  }

  private _updateViewDialogForm(gNames: EditGridView[]): void {
    if (this.dialogProps.viewAction === 'Edit') {
      /**
       * getFormGroup will generate a FormGroup
       * with FormControls name and id.
       * [k in keyof EditGridView] extract keys of EditGridView.
       * */
      const getFormGroup = (name: string, id: string): EditGridViewForm => {
        return this._fb.group<{
          [k in keyof EditGridView]: FormControl<string>;
        }>({
          id: new FormControl(id, { nonNullable: true }),
          name: new FormControl(name, {
            nonNullable: true,
            validators: [Validators.required],
          }),
        });
      };

      this.gridNames.update((arr) => {
        for (const g of gNames) {
          arr.push(getFormGroup(g.name as string, g.id as string));
        }
        return arr;
      });
    }
  }

  private _constructEdit(): void {
    let updatedGrids: GridView[] = [];
    if (this.gridNames().value.length > 0) {
      updatedGrids = this.dialogProps.data?.reduce((acc, d) => {
        for (const g of this.gridNames().value) {
          if (d.id == g.id) {
            acc.push({ ...d, name: g.name as string });
          }
        }
        return acc;
      }, [] as GridView[]) as GridView[];
    }
    this._store.dispatch(
      GRID_VIEW_ACTIONS.onBulkUpdateGridViews({
        grid_views: updatedGrids as GridView[],
        grid_type: this.dialogProps.gridProps?.grid_type as GridType,
      }),
    );
  }
}
